/**
 * Shared retailer slug -> display name resolution.
 *
 * Two consumers:
 *   - UI display ("JB Hi-Fi", "Amazon AU", "Dick Smith")     -> prettifyRetailerSlug()
 *   - Schema.org JSON-LD ("Amazon Australia", "Apple Australia") -> prettifyRetailerSeoName()
 *
 * Background: the same override map used to live duplicated across
 * ComparisonRow.tsx, BreakdownPanel.tsx, Hero.tsx, c/[slug]/page.tsx,
 * and lib/jsonld.ts. Five copies meant adding "Dick Smith" took five
 * edits and was always missed in at least one place (the comparison
 * page showed "Dick Smith" but the JSON-LD output said "Dicksmith Com
 * Au" - real prod symptom). Consolidating to one source.
 *
 * The map covers every retailer in the seed registry plus retailers we
 * have observed surfacing organically through SERP discovery. New
 * entries: append to RETAILERS below. Display + SEO can differ
 * intentionally (e.g. "Amazon AU" for the compact card vs. "Amazon
 * Australia" for SEO meta) - structure the entry as {display, seo} when
 * they should differ.
 */

interface RetailerName {
  display: string;
  seo?: string; // optional; falls back to display
}

const RETAILERS: Record<string, RetailerName> = {
  // ---- Tier 1 electronics / department -------------------------------
  "jb-hi-fi": { display: "JB Hi-Fi" },
  "the-good-guys": { display: "The Good Guys" },
  "harvey-norman": { display: "Harvey Norman" },
  "big-w": { display: "Big W" },
  officeworks: { display: "Officeworks" },
  bunnings: { display: "Bunnings" },
  "apple-au": { display: "Apple AU", seo: "Apple Australia" },
  "apple-education": { display: "Apple Education", seo: "Apple Education Store" },
  "ebay-au": { display: "eBay AU", seo: "eBay Australia" },
  // B-039 + B-032 (2026-05-19): retailer-owned eBay AU storefronts.
  // The agent path tags listings from these registered seller IDs with
  // the parent retailer's slug + "-ebay" suffix. Display name parens
  // make the routing clear to the user ("this is JB Hi-Fi, sold via
  // their eBay store"). Source list matches the 8 storefronts in
  // api/app/services/ebay_storefronts.py — keep in sync if one is
  // added or removed.
  "the-good-guys-ebay": { display: "The Good Guys (eBay AU)", seo: "The Good Guys via eBay Australia" },
  "jb-hi-fi-ebay": { display: "JB Hi-Fi (eBay AU)", seo: "JB Hi-Fi via eBay Australia" },
  "bing-lee-ebay": { display: "Bing Lee (eBay AU)", seo: "Bing Lee via eBay Australia" },
  "big-w-ebay": { display: "Big W (eBay AU)", seo: "Big W via eBay Australia" },
  "myer-ebay": { display: "Myer (eBay AU)", seo: "Myer via eBay Australia" },
  "dell-au-ebay": { display: "Dell (eBay AU)", seo: "Dell via eBay Australia" },
  "sony-au-ebay": { display: "Sony (eBay AU)", seo: "Sony via eBay Australia" },
  "samsung-au-ebay": { display: "Samsung (eBay AU)", seo: "Samsung via eBay Australia" },
  "amazon-au": { display: "Amazon AU", seo: "Amazon Australia" },
  "kmart-au": { display: "Kmart" },
  "target-au": { display: "Target" },
  "samsung-au": { display: "Samsung AU", seo: "Samsung Australia" },
  "sony-au": { display: "Sony AU", seo: "Sony Australia" },
  "lg-au": { display: "LG AU", seo: "LG Australia" },
  "bose-au": { display: "Bose AU", seo: "Bose Australia" },
  "dell-au": { display: "Dell AU", seo: "Dell Australia" },
  "hp-au": { display: "HP AU", seo: "HP Australia" },
  "bing-lee": { display: "Bing Lee" },
  "dicksmith-com-au": { display: "Dick Smith" },
  kogan: { display: "Kogan" },
  myer: { display: "Myer" },
  "myer-one": { display: "Myer One" },
  "david-jones": { display: "David Jones" },
  catch: { display: "Catch" },
  "rebel-sport": { display: "Rebel" },
  "the-iconic": { display: "The Iconic" },
  digidirect: { display: "Digidirect" },
  "camera-house": { display: "Camera House" },
  "teds-cameras": { display: "Teds Cameras" },
  "appliances-online": { display: "Appliances Online" },
  "winning-appliances": { display: "Winning Appliances" },
  "betta-home-living": { display: "Betta Home Living" },
  domayne: { display: "Domayne" },
  "joyce-mayne": { display: "Joyce Mayne" },
  "centre-com": { display: "Centre Com" },
  mwave: { display: "Mwave" },
  pccasegear: { display: "PCCaseGear" },
  "ple-computers": { display: "PLE Computers" },
  scorptec: { display: "Scorptec" },
  umart: { display: "Umart" },
  "costco-au": { display: "Costco" },

  // ---- Telcos --------------------------------------------------------
  "telstra-com-au": { display: "Telstra" },
  "optus-com-au": { display: "Optus" },
  "vodafone-com-au": { display: "Vodafone" },
  "amaysim-com-au": { display: "Amaysim" },

  // ---- Outdoor / hardware --------------------------------------------
  anaconda: { display: "Anaconda" },
  bcf: { display: "BCF" },
  kathmandu: { display: "Kathmandu" },
  macpac: { display: "Macpac" },
  snowys: { display: "Snowys" },
  tentworld: { display: "Tentworld" },
  "wild-earth": { display: "Wild Earth" },
  "mitre-10": { display: "Mitre 10" },
  "sydney-tools": { display: "Sydney Tools" },
  "total-tools": { display: "Total Tools" },
  "hare-and-forbes": { display: "Hare & Forbes" },

  // ---- Auto ----------------------------------------------------------
  "supercheap-auto": { display: "Supercheap Auto" },
  autobarn: { display: "Autobarn" },
  repco: { display: "Repco" },

  // ---- Fashion / lifestyle / beauty ----------------------------------
  "adidas-au": { display: "Adidas AU", seo: "Adidas Australia" },
  "nike-au": { display: "Nike AU", seo: "Nike Australia" },
  "lululemon-au": { display: "Lululemon AU", seo: "Lululemon Australia" },
  "asos-au": { display: "ASOS AU", seo: "ASOS Australia" },
  "zara-au": { display: "Zara AU", seo: "Zara Australia" },
  "cotton-on": { display: "Cotton On" },
  "country-road": { display: "Country Road" },
  "princess-polly": { display: "Princess Polly" },
  "lorna-jane": { display: "Lorna Jane" },
  "mj-bale": { display: "M.J. Bale" },
  politix: { display: "Politix" },
  "rm-williams": { display: "R.M.Williams" },
  sportsgirl: { display: "Sportsgirl" },
  witchery: { display: "Witchery" },
  "adore-beauty": { display: "Adore Beauty" },
  mecca: { display: "Mecca" },
  // B-212 (2026-09-13, owner): the accent, everywhere the name prints.
  lancome: { display: "Lancôme", seo: "Lancôme Australia" },
  "lancome-com-au": { display: "Lancôme", seo: "Lancôme Australia" },
  priceline: { display: "Priceline" },
  "chemist-warehouse": { display: "Chemist Warehouse" },

  // ---- Books / homewares / pets / kids -------------------------------
  dymocks: { display: "Dymocks" },
  "baby-bunting": { display: "Baby Bunting" },
  "pet-circle": { display: "Pet Circle" },
  petbarn: { display: "Petbarn" },
  petstock: { display: "Petstock" },
  "pillow-talk": { display: "Pillow Talk" },
  "temple-and-webster": { display: "Temple & Webster" },
  spotlight: { display: "Spotlight" },

  // ---- Liquor --------------------------------------------------------
  "dan-murphys": { display: "Dan Murphy's" },
  bws: { display: "BWS" },

  // ---- Loyalty / cashback (rarely surfaces on cards but kept) --------
  flybuys: { display: "Flybuys" },
  "everyday-rewards": { display: "Everyday Rewards" },
  "jb-perks": { display: "JB Perks" },
  "samsung-rewards": { display: "Samsung Rewards" },
  shopback: { display: "ShopBack" },
  topcashback: { display: "TopCashback" },

  // ---- Discovery-found (observed in prod, not in seed) ---------------
  // 2026-06-27: added so the card / My-requests names match the negotiate
  // modal + email (backend retailer_names.py maps these too). Without the
  // override the generic title-case gave "Jd Sports" / "Footlocker".
  "jd-sports-com-au": { display: "JD Sports" },
  footlocker: { display: "Foot Locker" },
  "backmarket-com-au": { display: "Back Market" },
  "cashaway-com-au": { display: "Cashaway" },
  "ozmobiles-com-au": { display: "Ozmobiles" },
  "phonebot-com-au": { display: "Phonebot" },
  "mobileciti-com-au": { display: "Mobileciti" },
  "becextech-com-au": { display: "BecexTech" },
};

/**
 * Strip noisy domain-derived suffixes that auto-slugged retailer
 * registrations leave behind ("cashaway-com-au" -> "cashaway"). Only
 * applied when the slug is NOT in the override map; explicit overrides
 * always win. Order matters: longest suffix first so we don't half-match.
 */
const NOISE_SUFFIXES = ["-com-au", "-com-au-seller", "-com", "-au"];

function stripNoiseSuffix(slug: string): string {
  for (const suffix of NOISE_SUFFIXES) {
    if (slug.endsWith(suffix) && slug.length > suffix.length) {
      return slug.slice(0, -suffix.length);
    }
  }
  return slug;
}

function titleCase(slug: string): string {
  return slug
    .split("-")
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
    .join(" ");
}

/**
 * UI display form: short, comparison-card friendly.
 * "jb-hi-fi" -> "JB Hi-Fi", "cashaway-com-au" -> "Cashaway".
 */
export function prettifyRetailerSlug(slug: string): string {
  if (!slug) return slug;
  const entry = RETAILERS[slug];
  if (entry) return entry.display;
  return titleCase(stripNoiseSuffix(slug));
}

/**
 * SEO / JSON-LD form: long-form variant used in Schema.org `seller.name`
 * so Google Shopping / Bing rich results read naturally. Falls back to
 * the display form when no SEO variant is declared.
 */
export function prettifyRetailerSeoName(slug: string): string {
  if (!slug) return slug;
  const entry = RETAILERS[slug];
  if (entry) return entry.seo ?? entry.display;
  return titleCase(stripNoiseSuffix(slug));
}
